import { createContext } from 'react';
import {
  BusinessPluginApp,
  DefaultConfig,
  FeatureFlags,
  ObjectSettingsMenuItems,
  PluginBlockEntries,
  PluginConfig,
  RouteScripts,
  ToolbarItem,
  ToolbarItems,
} from './types';
import type { JSONValue, OnShowModalFn } from '@kizenapps/engine';
import type { RegisterInlinePages } from './inlinePages';

type PluginContextValue = {
  isLoading: boolean;
  isFetched: boolean;
  sessionInvalid: boolean;
  pluginNames: string[];
  floatingFrames: DefaultConfig['floatingFrames'];
  routablePages: DefaultConfig['routablePages'];
  dataAdornments: DefaultConfig['dataAdornments'];
  calendarSources: DefaultConfig['calendarSources'];
  refetch: () => void;
  fullRefetch: (pluginApiName?: string) => void;
  refreshPluginArtifacts: (
    pluginApiName?: string,
    options?: { pluginAppId?: string }
  ) => Promise<void>;
  installedPluginAPINamesToIds: Record<string, string>;
  hiddenByModal: boolean;
  // True while a page surface (the AI block editor) suppresses the plugin
  // overlays; see overlaySuppression.ts
  hiddenByEditor: boolean;
  // Hides the plugin overlays until the returned release is called
  suppressOverlays: () => () => void;
  // Makes pages resolvable by the host plugin modal (never by routing or
  // navigation) until the returned release is called; see inlinePages.ts
  registerInlinePages: RegisterInlinePages;
  plugins: PluginConfig;
  toolbarItems: ToolbarItems;
  toolbarItemsByFullId: Record<string, ToolbarItem>;
  handleShowModal: OnShowModalFn;
  waitingOnRouteScript: boolean;
  routeScripts: RouteScripts;
  automationSteps: DefaultConfig['automationSteps'];
  pluginBlocks: PluginBlockEntries;
  handleShowCreateRecordModal: (
    objectId: string,
    cb: (result: any) => void
  ) => void;
  handleShowCreateRelatedRecordModal: (
    objectId: string,
    relatedEntityId: string,
    cb: (result: any) => void
  ) => void;
  objectSettingsMenuItems: ObjectSettingsMenuItems;
  userConfigsByApiName: Record<string, Record<string, any>>;
  featureFlags: FeatureFlags | undefined;
  refetchFeatureFlags: () => void;
  featureFlagsLoading: boolean;
  businessPluginAppsByApiName: Record<string, BusinessPluginApp>;
  plan: Record<string, Record<string, JSONValue>>;
  entitlements: Record<string, JSONValue>;
  businessPluginAppsById: Record<string, BusinessPluginApp>;
};

export const PluginContext = createContext<PluginContextValue>({
  isFetched: false,
  isLoading: true,
  sessionInvalid: false,
  pluginNames: [],
  floatingFrames: [],
  routablePages: [],
  dataAdornments: [],
  calendarSources: [],
  refetch: () => {},
  fullRefetch: () => {},
  refreshPluginArtifacts: async () => {},
  installedPluginAPINamesToIds: {},
  hiddenByModal: false,
  hiddenByEditor: false,
  suppressOverlays: () => () => {},
  registerInlinePages: () => () => {},
  plugins: {},
  toolbarItems: [],
  toolbarItemsByFullId: {},
  handleShowModal: () => {},
  waitingOnRouteScript: false,
  routeScripts: [],
  automationSteps: [],
  pluginBlocks: [],
  handleShowCreateRecordModal: () => {},
  handleShowCreateRelatedRecordModal: () => {},
  objectSettingsMenuItems: [],
  userConfigsByApiName: {},
  featureFlags: {},
  refetchFeatureFlags: () => {},
  featureFlagsLoading: true,
  businessPluginAppsByApiName: {},
  plan: {},
  entitlements: {},
  businessPluginAppsById: {},
});
